// src/utils/api.ts
import axios, {
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosHeaders,
} from 'axios';
import { getAuthToken, clearAuthToken } from './tokenStorage';

// Get API base URL from environment or default
export const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (window.location.hostname.includes("local")
    ? "https://api.blackadder.local:8890/api"
    : "https://api.blackadder.co.jp/api");

/**
 * Client for making authenticated API requests
 * Integrated with account-app cookie-based authentication
 */
export const apiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,  // Important for cross-domain cookie handling
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }
});

// Add interceptor to set the Authorization header from stored token
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAuthToken();

  const headers =
    config.headers instanceof AxiosHeaders
      ? config.headers
      : AxiosHeaders.from(config.headers || {});

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  } else if (headers.get('Authorization') === "Bearer null") {
    headers.delete('Authorization');
  }

  config.headers = headers;

  return config;
}, (error: AxiosError) => Promise.reject(error));

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response && error.response.status === 401) {
      clearAuthToken();
    }
    return Promise.reject(error);
  }
);

/**
 * Make a GET request to the API
 * @param url The endpoint URL
 * @param config Optional Axios config
 * @returns Promise with the response data
 */
export const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.get<T>(url, config);
  return response.data;
};

/**
 * Make a POST request to the API
 * @param url The endpoint URL
 * @param data The data to send
 * @param config Optional Axios config
 * @returns Promise with the response data
 */
export const post = async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
};

/**
 * Make a PUT request to the API
 * @param url The endpoint URL
 * @param data The data to send
 * @param config Optional Axios config
 * @returns Promise with the response data
 */
export const put = async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.put<T>(url, data, config);
  return response.data;
};

/**
 * Make a DELETE request to the API
 * @param url The endpoint URL
 * @param config Optional Axios config
 * @returns Promise with the response data
 */
export const del = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
};

const api = {
  get,
  post,
  put,
  delete: del,
};

export default api;
